import { cn } from '../lib/utils'

// Shared build-stamp badge. Reads the version footprint that each app's
// vite.config bakes in via scripts/viteVersionDefine.mjs (import.meta.env.VITE_*).
// The `(import.meta as any).env` cast matches the rest of packages/ui, which
// doesn't pull in vite/client's ambient types.
//
//   <VersionBadge />                 top-bar variant — STAGING ONLY
//   <VersionBadge variant="muted" /> always-on muted stamp (footers/drawers)
//
// Shows "v{version} · {build date & time}" so at a glance you see both what
// build is running and when it was made; the tooltip adds the git SHA, the
// shared @meno/ui version, the environment, and the full ISO timestamp.

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}
const APP_VERSION = env.VITE_APP_VERSION || '0.0.0'
const UI_VERSION = env.VITE_UI_VERSION || '0.0.0'
const GIT_SHA = env.VITE_GIT_SHA || 'dev'
const APP_ENV = env.VITE_APP_ENV || 'development'
const BUILD_TIME = env.VITE_BUILD_TIME || ''

function formatBuildTime(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

export interface VersionBadgeProps {
  /** 'topbar' (default) renders only on staging; 'muted' always renders. */
  variant?: 'topbar' | 'muted'
  className?: string
}

export function VersionBadge({ variant = 'topbar', className }: VersionBadgeProps) {
  const isStaging = APP_ENV === 'staging'
  // The prominent top-bar stamp is a staging-only affordance; prod stays clean.
  if (variant === 'topbar' && !isStaging) return null

  const when = formatBuildTime(BUILD_TIME)
  const label = when ? `v${APP_VERSION} · ${when}` : `v${APP_VERSION}`
  const title = [
    `Version ${APP_VERSION}`,
    `commit ${GIT_SHA}`,
    `@meno/ui ${UI_VERSION}`,
    `env ${APP_ENV}`,
    BUILD_TIME && `built ${BUILD_TIME}`,
  ].filter(Boolean).join('\n')

  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[10px] leading-none select-none whitespace-nowrap',
        isStaging
          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
          : 'text-muted-foreground',
        className,
      )}
    >
      {isStaging && <span className="font-semibold uppercase tracking-wide">staging</span>}
      <span className="tabular-nums">{label}</span>
    </span>
  )
}

export default VersionBadge
